// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import './styles.css';
import type { ClimateData } from './types';
import { loadData } from './data';
import { initTooltip } from './tooltip';
import { initGlossary, GLOSSARY, gloss, hideGloss } from './glossary';
import { openDrawer, closeDrawer } from './drawer';
import { esc } from './format';

import { mount as overview } from './views/overview';
import { mount as stripes } from './views/stripes';
import { mount as map } from './views/map';
import { mount as nightsdays } from './views/nightsdays';
import { mount as hotcold } from './views/hotcold';
import { mount as seasons } from './views/seasons';
import { mount as rankings } from './views/rankings';
import { mount as explorer } from './views/explorer';
import { mount as insights } from './views/insights';

type ViewFn = (el: HTMLElement, data: ClimateData) => void | (() => void);
interface Tab { id: string; label: string; fn: ViewFn }

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', fn: overview },
  { id: 'stripes', label: 'Stripes', fn: stripes },
  { id: 'map', label: 'Map', fn: map },
  { id: 'nights', label: 'Nights vs Days', fn: nightsdays },
  { id: 'hotcold', label: 'Hot & Cold', fn: hotcold },
  { id: 'seasons', label: 'Seasons', fn: seasons },
  { id: 'rankings', label: 'Rankings', fn: rankings },
  { id: 'explorer', label: 'Explorer', fn: explorer },
  { id: 'insights', label: 'Insights', fn: insights },
];

function parseHash(): { view: string; s: string | null } {
  const p = new URLSearchParams(location.hash.replace(/^#/, ''));
  return { view: p.get('view') || 'overview', s: p.get('s') };
}

let cleanup: (() => void) | void;
let data: ClimateData;

function renderShell(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#view=overview">
          <span class="brand-stripes" aria-hidden="true">${'<i></i>'.repeat(9)}</span>
          <span class="brand-txt"><strong>Warming</strong><small>Australia's temperature record</small></span>
        </a>
        <nav class="tabs" data-tabs aria-label="Views">
          ${TABS.map((t) => `<a href="#view=${t.id}" data-tab="${t.id}">${t.label}</a>`).join('')}
        </nav>
        <button class="about-btn" data-about aria-label="About this site">?</button>
      </div>
    </header>
    <main class="main-content" data-main></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>Source: Bureau of Meteorology — <a href="http://www.bom.gov.au/climate/data/acorn-sat/" target="_blank" rel="noopener">ACORN-SAT</a> (CC BY 4.0). Not affiliated with the Bureau.</span>
        <span>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
      </div>
    </footer>`;

  app.querySelector('[data-about]')?.addEventListener('click', openAbout);
  window.addEventListener('hashchange', route);
}

function setActiveTab(view: string): void {
  document.querySelectorAll('[data-tab]').forEach((a) => a.classList.toggle('active', a.getAttribute('data-tab') === view));
}

function route(): void {
  const { view, s } = parseHash();
  const tab = TABS.find((t) => t.id === view) || TABS[0];
  const main = document.querySelector('[data-main]') as HTMLElement;
  const prevView = main.getAttribute('data-view');

  if (prevView !== tab.id) {
    // Hygiene on view change: drop any open modal, stale hover tooltip or popover.
    document.querySelector('.modal-host')?.remove();
    document.querySelector('.hover-tip.visible')?.classList.remove('visible');
    hideGloss();
    if (cleanup) { cleanup(); cleanup = undefined; }
    main.innerHTML = '';
    setActiveTab(tab.id);
    main.setAttribute('data-view', tab.id);
    try {
      cleanup = tab.fn(main, data) || undefined;
    } catch (err) {
      main.innerHTML = `<div class="error">Something went wrong rendering this view.</div>`;
      console.error(err);
    }
    window.scrollTo({ top: 0 });
  }

  if (s) openDrawer(data, s);
  else closeDrawer(false);
}

function openAbout(): void {
  const glossItems = Object.values(GLOSSARY).map((g) => `<dt>${esc(g.term)}</dt><dd>${esc(g.def)}</dd>`).join('');
  const host = document.createElement('div');
  host.className = 'modal-host';
  host.innerHTML = `
    <div class="modal-overlay" data-mclose></div>
    <div class="modal" role="dialog" aria-modal="true" aria-label="About this site">
      <div class="modal-head"><h2>About this site</h2><button data-mclose class="modal-close" aria-label="Close">✕</button></div>
      <div class="modal-body">
        <p><strong>Warming</strong> turns the Bureau of Meteorology's official temperature record into something you can actually see: how much hotter Australia — and your part of it — has become since 1910.</p>
        <h3>The data</h3>
        <p>Every figure comes from ${gloss('acorn', 'ACORN-SAT')}, the Bureau's quality-controlled, ${gloss('homogenised', 'homogenised')} record for ${data.meta.stationCount} long-running ${gloss('station', 'reference stations')}. We download each station's daily maximum and minimum temperatures, average them into months and years, and compare each year to its ${gloss('baseline', '1961–1990 average')}. Data currently runs through <strong>${data.meta.dataThrough}</strong>.</p>
        <h3>How it updates</h3>
        <p>The Bureau refreshes ACORN-SAT once a year (around February–March). An automated pipeline re-downloads and re-computes everything on that schedule, so the site follows the official record.</p>
        <h3>Important caveats</h3>
        <ul>
          <li>The <strong>national</strong> figures are a simple average across all stations — a fair approximation, but not the Bureau's area-weighted gridded analysis, so they can differ slightly from official national numbers.</li>
          <li>${gloss('homogenised', 'Homogenisation')} adjusts raw readings for site moves and equipment changes, so a station's record high here can differ from its raw all-time record.</li>
          <li>Anomalies need a full 1961–1990 baseline; a few stations that began later are shown in the Explorer but omitted from the stripes and national average.</li>
          <li>A "${gloss('frost', 'frost night')}" is defined here as an overnight minimum of 2 °C or below (a common frost-risk proxy), not a measured ground frost.</li>
        </ul>
        <h3>Glossary</h3>
        <dl class="gloss-list">${glossItems}</dl>
        <p class="muted">Temperature data © Commonwealth of Australia, Bureau of Meteorology (CC BY 4.0). Boundaries: ABS ASGS (CC BY 4.0). Basemap © CARTO / © OpenStreetMap contributors.</p>
      </div>
    </div>`;
  document.body.appendChild(host);
  const close = () => host.remove();
  host.querySelectorAll('[data-mclose]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', function onKey(e) { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); } });
}

async function boot(): Promise<void> {
  renderShell();
  const main = document.querySelector('[data-main]') as HTMLElement;
  main.innerHTML = '<div class="loading"><span class="spin"></span> Loading Australia\'s temperature record…</div>';
  initTooltip();
  initGlossary();
  try {
    data = await loadData();
  } catch (err) {
    main.innerHTML = `<div class="error">Could not load the data. Please try again later.</div>`;
    console.error(err);
    return;
  }
  route();
}

boot();
